import { useCallback, useEffect, useState } from 'react';
import { Backdrop, Box, CircularProgress, useTheme } from '@mui/material';
import Header from '../../components/Header';
import { useCookiesLogin } from '../../config/cookies';
import { formatCNPJ } from '../redscore';
import { useTranslation } from '../../hooks/useTranslation';
import HistoryModal from './HistoryModal';
import ConsultFilters from './consultFilters';
import ConsultTable from './consultTable';
import api from '../../services/api';

const ConsultList = () => {
  const { cookies } = useCookiesLogin();
  const { last_company: bussines_id } = cookies?.AUTH_TOKEN || {};
  const { t } = useTranslation();

  const [consults, setConsults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros e paginação:
  const [documentFilter, setDocumentFilter] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [showFiltered, setShowFiltered] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loadingClientData, setLoadingClientData] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    if (documentFilter === '') {
      setShowFiltered(false);
      setFilteredRows([]);
      return;
    }

    const filtered = consults.filter(
      (item) => formatCNPJ(item?.customerData?.taxvar) === formatCNPJ(documentFilter)
    );

    setFilteredRows(filtered);
    setShowFiltered(true);
    setPage(0);
  };

  const fetchConsults = useCallback(async () => {
    try {
      setLoading(true);
      const { data: resultado } = await api.get('/v1/order');
      setConsults(resultado.list);
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
    } finally {
      setLoading(false);
    }
  }, [bussines_id]);

  useEffect(() => {
    fetchConsults();
  }, [fetchConsults]);

  const handleOpenModal = async (row) => {
    setSelectedRow(row);
    setModalOpen(true);
    if (row) {
      setLoadingClientData(true);
      const { token } = cookies.AUTH_TOKEN;

      try {
        const { data: response } = await api.get(`/v1/order/${row.id}/edit`);
        const { data: consultData, ...restConsult } = response?.consult || {};
        const clientData = {
          ...restConsult,
          ...consultData,
        };

        setSelectedRow((prev) => ({
          ...prev,
          clientDataLoaded: true,
          clientName: clientData?.name,
          taxIdStatus: clientData?.tax_id_status,
          eventProbability: clientData?.event_probability,
          regime: clientData?.tax_regime,
          riskScreening: clientData?.risk_screening,
          addresses: clientData?.addresses || [],
          phones: clientData?.phones || [],
          emails: clientData?.emails || [],
          birthDate: clientData?.birthDate,
          motherName: clientData?.motherName,
          taxIdOrigin: clientData?.taxIdOrigin,
          nameUniquenessScore: clientData?.nameUniquenessScore,
          relatedPeople: clientData?.relatedPeople || [],
          businessRelationships: clientData?.businessRelationships || [],
          tradeName: clientData?.tradeName,
          legalNature: clientData?.legalNature,
          taxIdStatusDate: clientData?.taxIdStatusDate,
          registrationDate: clientData?.taxIdStatusRegistrationDate,
          activities: clientData?.activities || [],
          capital: clientData?.capital,
          companySize: clientData?.companySize,
          operabilityIndicator: clientData?.operabilityIndicator,
          partnersAndShareholders: clientData?.partnersAndShareholders || [],
          partnerParticipation: clientData?.partnerParticipation || [],
          protestOccurrences: clientData?.protest?.occurrences || 0,
          judicialActions: clientData?.judicialAction?.occurrences || 0,
          financialRestrictions: clientData?.financialRestrictions?.occurrences || 0,
          bankruptcies: clientData?.bankruptcies?.occurrences || 0,
          financialPendencies: clientData?.financialPendencies?.occurrences || 0,
          financialPendenciesDetails:
            clientData?.financialPendencies?.financialPendenciesDetails ||
            clientData?.financialPendencies?.financialPendenciessDetails ||
            [],
          blockedByDataOwner: clientData?.blockedByDataOwner || false,
          observation: response?.observation || '',
        }));
      } catch (err) {
        console.error(t('messages.errorFetchingClientData'), err);
      } finally {
        setLoadingClientData(false);
      }
    }

    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  if (loading) {
    return (
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Header title={t('consultation.historyRedscore')} />

      <ConsultFilters
        documentFilter={documentFilter}
        onDocumentChange={setDocumentFilter}
        onSearch={handleSearch}
      />

      <ConsultTable
        data={consults}
        filteredData={filteredRows}
        showFiltered={showFiltered}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRowClick={handleOpenModal}
      />

      <HistoryModal
        open={modalOpen}
        handleClose={handleCloseModal}
        row={selectedRow}
        loading={loadingClientData}
      />
    </Box>
  );
};

export default ConsultList;
